import { resolveFromNames } from './chatSearchQuery';

/**
 * Turn a parsed search (chatSearchQuery.parseSearchQuery) into the search
 * endpoint's structured params (PLAN §5.1).
 *
 * Dates are calendar days in the VIEWER's timezone, which only the client
 * knows (D5). The server takes UTC instants: `before` exclusive, `after`
 * inclusive.
 *   before:D → before = start(D)
 *   after:D  → after  = start(D + 1)
 *   during:D → after  = start(D), before = start(D + 1)
 * Several date tokens intersect (the latest lower bound, the earliest upper).
 *
 * → { params, blocked, blockedValue }. `blocked` is null or one of
 * 'badDate' | 'unknownUser' | 'empty'; nothing is sent when it is set.
 */

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A local calendar day { y, m (0-based), d } for a token value, or null. */
export function parseDay(value, now = new Date()) {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'today') return { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() };
  if (v === 'yesterday') {
    const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    return { y: y.getFullYear(), m: y.getMonth(), d: y.getDate() };
  }
  const match = DAY_RE.exec(v);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  const probe = new Date(y, m, d);
  // Rejects 2026-02-30 (Date would roll it into March).
  if (probe.getFullYear() !== y || probe.getMonth() !== m || probe.getDate() !== d) return null;
  return { y, m, d };
}

/** Local midnight of `day` plus `offset` days, as a UTC ISO instant. */
export function startOfDay(day, offset = 0) {
  return new Date(day.y, day.m, day.d + offset).toISOString();
}

/** 'YYYY-MM-DD' for a Date in local time (the token format). */
export function dayToken(date) {
  const p = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

const maxIso = (a, b) => (a == null ? b : b == null ? a : a > b ? a : b);
const minIso = (a, b) => (a == null ? b : b == null ? a : a < b ? a : b);

export function toSearchParams(parsed, members, { now = new Date() } = {}) {
  const block = (reason, value = null) => ({ params: null, blocked: reason, blockedValue: value });
  let after = null;
  let before = null;
  const bounds = [
    ['before', (day) => (before = minIso(before, startOfDay(day)))],
    ['after', (day) => (after = maxIso(after, startOfDay(day, 1)))],
    [
      'during',
      (day) => {
        after = maxIso(after, startOfDay(day));
        before = minIso(before, startOfDay(day, 1));
      },
    ],
  ];
  for (const [key, apply] of bounds) {
    for (const value of parsed[key] || []) {
      const day = parseDay(value, now);
      if (!day) return block('badDate', value);
      apply(day);
    }
  }

  let from = [];
  if ((parsed.from || []).length) {
    const { ids, unresolved } = resolveFromNames(parsed.from, members);
    if (unresolved.length) return block('unknownUser', unresolved[0]);
    from = ids;
  }

  const has = Array.from(new Set(parsed.has || []));
  const q = (parsed.text || '').trim();
  if (!q && !has.length && !from.length && !before && !after) return block('empty');

  const params = {};
  if (q) params.q = q;
  if (has.length) params.has = has;
  if (from.length) params.from = from;
  if (before) params.before = before;
  if (after) params.after = after;
  return { params, blocked: null, blockedValue: null };
}
